import { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api, absUrl } from "@/lib/api";
import { SafeVideo } from "@/components/SafeVideo";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Play,
  Download,
  Trash2,
  PlusCircle,
  FolderOpen,
  Loader2,
  RefreshCcw,
  Tag,
  Youtube,
  ExternalLink,
  Wand2,
  Hourglass,
  AlertTriangle,
  AlertCircle,
} from "lucide-react";
import PublishDialog from "@/components/PublishDialog";
import AnchorConfirmPanel from "@/components/AnchorConfirmPanel";
import RegenerateVideoButton from "@/components/RegenerateVideoButton";
import ManualPromoteButton from "@/components/ManualPromoteButton";
import KillswitchBanner from "@/components/KillswitchBanner";
import JobProgressBar from "@/components/JobProgressBar";

const TYPE_COLORS = {
  story: "bg-amber-100 text-amber-800",
  music: "bg-rose-100 text-rose-800",
  learning: "bg-emerald-100 text-emerald-800",
};

function hasFallbacks(v) {
  return (
    v.clip_provider === "ken_burns" ||
    (v.clip_stats && (v.clip_stats.ken_burns_fallback || 0) > 0) ||
    v.is_mocked === true ||
    v.outro_mode === "static"
  );
}

// Phase1Observability1:progress_eta_v1 — needs_human helpers
const GUARD_LABELS = {
  G2: "pacing",
  G3: "audio assets",
  G4: "duration",
  G5: "scene count",
  G6: "dead zones",
  G7: "outro CTA",
  G8: "thumbnail",
  G9: "motion",
  G10: "library hygiene",
  G11: "auto-rearm",
  G12: "worker readiness",
  G13: "test marker leak",
};

function humanReviewReason(raw) {
  if (!raw) return null;
  const s = String(raw).toLowerCase();
  if (s.startsWith("phase4_regen_multi_guard_fail")) return "regen failed quality checks";
  if (s.includes("thumbnail_generation_failed"))     return "thumbnail couldn't be generated";
  if (s.includes("count_verifier") || s.includes("scene_count")) return "scene count didn't match script";
  if (s.includes("forbidden_token") || s.includes("test_dom"))   return "test marker leaked into final assets";
  if (s.includes("duration_gate") || s.includes("min_duration")) return "video too short for long-form";
  if (s.includes("motion_intensity") || s.includes("dead_zone")) return "video has dead-motion zones";
  if (s.includes("outro_cta"))                       return "outro CTA missing or silent";
  if (s.includes("thumbnail_present"))               return "thumbnail missing or unreadable";
  return raw.length > 60 ? raw.slice(0, 57) + "…" : raw;
}

function parseGuardChips(raw) {
  if (!raw || typeof raw !== "string") return [];
  // Match patterns like phase4_regen_multi_guard_fail_G4_G6_G7_G8_G9
  const codes = Array.from(raw.matchAll(/\bG(\d+)\b/g)).map((m) => `G${m[1]}`);
  const seen = new Set();
  const chips = [];
  codes.forEach((c) => {
    if (seen.has(c)) return;
    seen.add(c);
    chips.push({ code: c, label: GUARD_LABELS[c] || "guard" });
  });
  return chips;
}

function QualityBadge({ video }) {
  if (video.status === "awaiting_retry" || video.awaiting_retry) {
    return (
      <Badge
        data-testid={`quality-retry-${video.id}`}
        className="bg-amber-500 hover:bg-amber-500"
      >
        <Hourglass className="mr-1 h-3 w-3" /> awaiting retry
      </Badge>
    );
  }
  if (video.status === "failed" && video.failure_reason) {
    return (
      <Badge
        data-testid={`quality-failed-${video.id}`}
        className="bg-red-600 hover:bg-red-600"
      >
        <AlertTriangle className="mr-1 h-3 w-3" /> provider unavailable
      </Badge>
    );
  }
  if (video.status === "ready" && hasFallbacks(video)) {
    const capCount = (video.clip_stats || {}).ken_burns_cap_fallback || 0;
    if (capCount > 0) {
      return (
        <Badge
          data-testid={`quality-cap-fallback-${video.id}`}
          className="bg-yellow-200 text-yellow-900 hover:bg-yellow-200"
          title="Hit cost cap — used Ken Burns instead of fal.ai for the over-cap scenes"
        >
          <AlertTriangle className="mr-1 h-3 w-3" />
          Hit cost cap — {capCount} scene{capCount === 1 ? "" : "s"} Ken Burns
        </Badge>
      );
    }
    return (
      <Badge
        data-testid={`quality-fallback-${video.id}`}
        className="bg-amber-100 text-amber-900 hover:bg-amber-100"
      >
        <AlertTriangle className="mr-1 h-3 w-3" /> has fallbacks
      </Badge>
    );
  }
  if (video.status === "ready") {
    return (
      <Badge
        data-testid={`quality-animated-${video.id}`}
        className="bg-emerald-100 text-emerald-900 hover:bg-emerald-100"
      >
        <Wand2 className="mr-1 h-3 w-3" /> fully animated
      </Badge>
    );
  }
  return null;
}

export default function Library() {
  const [videos, setVideos] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [type, setType] = useState("all");
  const [status, setStatus] = useState("all");
  const [qualityFilter, setQualityFilter] = useState("all");
  const [sortMode, setSortMode] = useState("episode"); // "episode" | "newest"
  const [groupByType, setGroupByType] = useState(false);
  const [open, setOpen] = useState(null);
  const [publishOpen, setPublishOpen] = useState(null);
  const [ytConnected, setYtConnected] = useState(false);
  const [regenLoading, setRegenLoading] = useState(false);
  // Phase 2 preview UX — fallback duration read from the actual <video>
  // element when Mongo's duration_sec is stale/0/null. Cleared whenever
  // the modal opens a different doc.
  const [previewDur, setPreviewDur] = useState(null);
  const [previewError, setPreviewError] = useState(null);
  useEffect(() => { setPreviewDur(null); setPreviewError(null); }, [open?.id]);
  const [sp] = useSearchParams();

  const refresh = async () => {
    const params = {};
    if (type !== "all") params.type = type;
    if (status !== "all") params.status = status;
    if (qualityFilter === "fallbacks") params.has_fallbacks = true;
    if (qualityFilter === "retry") params.awaiting_retry = true;
    // 'animated' and 'failed' are post-filtered client-side because they
    // need a compound check (has_fallbacks=false AND status=ready).
    const r = await api.get("/videos", { params });
    let rows = r.data;
    if (qualityFilter === "animated") {
      rows = rows.filter(
        (v) => v.status === "ready" && !hasFallbacks(v),
      );
    } else if (qualityFilter === "unavailable") {
      rows = rows.filter(
        (v) => v.status === "failed" || v.failure_reason,
      );
    }
    setVideos(rows);
    setLoaded(true);
    const highlight = sp.get("highlight");
    if (highlight) {
      const found = rows.find((v) => v.id === highlight);
      if (found) setOpen(found);
    }
  };

  useEffect(() => {
    refresh();
    // Sync URL ?awaiting_retry=1 from Dashboard click into our state
    if (sp.get("awaiting_retry") === "1" && qualityFilter !== "retry") {
      setQualityFilter("retry");
    }
  }, [type, status, qualityFilter]);

  useEffect(() => {
    api
      .get("/youtube/oauth/status")
      .then((r) => setYtConnected(!!r.data.connected))
      .catch(() => {});
  }, []);

  const onDelete = async (id) => {
    if (!window.confirm("Delete this video permanently?")) return;
    // CRITICAL: tear the modal down BEFORE issuing the DELETE so React
    // unmounts the <video> (running SafeVideo's cleanup: pause + clear
    // src + load) before the file is removed from disk on the backend.
    // Reverse order races with WebKit's media controller and triggers
    // `Can't find variable: EmptyRanges`.
    if (open?.id === id) setOpen(null);
    try {
      await api.delete(`/videos/${id}`);
    } finally {
      refresh();
    }
  };

  const filterOptions = useMemo(
    () => [
      { k: "all", l: "All" },
      { k: "music", l: "Music" },
      { k: "story", l: "Story" },
      { k: "learning", l: "Learning" },
    ],
    [],
  );
  const statusOptions = useMemo(
    () => [
      { k: "all", l: "All" },
      { k: "draft", l: "Drafts" },
      { k: "ready", l: "Ready" },
      { k: "failed", l: "Failed" },
    ],
    [],
  );

  // Phase 3 — sort by episode_number ascending (default), or by
  // updated_at descending. Optional grouping splits Music vs Story.
  const sortedVideos = useMemo(() => {
    const epAsc = (a, b) => {
      const ea = typeof a.episode_number === "number" ? a.episode_number : 1e9;
      const eb = typeof b.episode_number === "number" ? b.episode_number : 1e9;
      if (ea !== eb) return ea - eb;
      const ta = new Date(a.created_at || 0).getTime();
      const tb = new Date(b.created_at || 0).getTime();
      return ta - tb;
    };
    const newestFirst = (a, b) => {
      const ta = new Date(a.updated_at || a.created_at || 0).getTime();
      const tb = new Date(b.updated_at || b.created_at || 0).getTime();
      return tb - ta;
    };
    const cmp = sortMode === "newest" ? newestFirst : epAsc;
    return [...videos].sort(cmp);
  }, [videos, sortMode]);

  const groupedVideos = useMemo(() => {
    if (!groupByType) return null;
    const buckets = { music: [], story: [], learning: [], other: [] };
    for (const v of sortedVideos) {
      const t = (v.type || "").toLowerCase();
      (buckets[t] || buckets.other).push(v);
    }
    return buckets;
  }, [sortedVideos, groupByType]);

  if (!loaded) {
    return (
      <div className="flex justify-center py-20 text-stone-500">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  // Phase 3 — extracted card renderer so it can be reused by both
  // flat (sorted) and grouped (by type) layouts.
  const renderCard = (v) => (
    <Card
      key={v.id}
      data-testid={`video-${v.id}`}
      className="group overflow-hidden border-orange-200 bg-white/80 transition hover:shadow-md"
    >
      <button
        type="button"
        onClick={() => setOpen(v)}
        className="block aspect-video w-full overflow-hidden bg-gradient-to-br from-amber-100 via-orange-100 to-rose-100"
      >
        {/* P0-4 (2026-05-06): always render an <img>; if neither
            custom_thumbnail_url nor thumbnail_url is set, fall through
            directly to the t=15s preview-frame extractor. The onError
            handler also covers the case where thumbnail_url is set but
            the file doesn't exist on disk (404). */}
        <img
          src={absUrl(
            v.custom_thumbnail_url
              || v.thumbnail_url
              || `/api/videos/${v.id}/preview-frame.jpg`
          )}
          alt=""
          className="h-full w-full object-cover transition group-hover:scale-105"
          loading="lazy"
          onError={(e) => {
            const img = e.currentTarget;
            const fallback = absUrl(`/api/videos/${v.id}/preview-frame.jpg`);
            if (img.dataset.triedFallback !== "1" && img.src !== fallback) {
              img.dataset.triedFallback = "1";
              img.src = fallback;
            } else {
              // Final fallback: hide the broken img so the gradient bg shows.
              img.style.display = "none";
            }
          }}
        />
      </button>
      <CardHeader className="p-3 pb-0">
        <CardTitle className="line-clamp-2 text-sm">{v.title}</CardTitle>
      </CardHeader>
      <CardContent className="flex items-center justify-between p-3">
        <div className="flex items-center gap-1">
          <Badge
            variant="secondary"
            className={`${TYPE_COLORS[v.type] || ""} text-xs`}
          >
            {v.type}
          </Badge>
          <Badge
            className={
              v.status === "ready"
                ? "bg-emerald-600 hover:bg-emerald-600"
                : v.status === "failed"
                ? "bg-red-600 hover:bg-red-600"
                : v.status === "awaiting_retry"
                ? "bg-amber-500 hover:bg-amber-500"
                : "bg-amber-500 hover:bg-amber-500"
            }
          >
            {v.status}
          </Badge>
          {/* Phase6AuditPill */}
          {v.status === "ready" && typeof v.audit_passed !== "undefined" && (
            <Badge
              data-testid={`audit-pill-${v.id}`}
              className={v.audit_passed ? "bg-green-600 text-white hover:bg-green-600" : "bg-red-600 text-white hover:bg-red-600"}
              title={v.audit_passed ? "5-layer audit passed" : "Audit failed — see backend audit report"}
            >
              {v.audit_passed ? "Audit clean" : "Audit failed"}
            </Badge>
          )}
          <QualityBadge video={v} />
          {typeof v.episode_number === "number" ? (
            <Badge
              data-testid={`episode-badge-${v.id}`}
              className="bg-indigo-600 text-white hover:bg-indigo-600"
            >
              Ep {v.episode_number}
            </Badge>
          ) : null}
          {v.publish_status?.status === "published" ? (
            <Badge
              data-testid={`yt-badge-${v.id}`}
              className="bg-red-600 hover:bg-red-600"
            >
              <Youtube className="mr-1 h-3 w-3" /> live
            </Badge>
          ) : v.publish_status?.status === "scheduled" ? (
            <Badge className="bg-sky-600 hover:bg-sky-600">
              <Youtube className="mr-1 h-3 w-3" /> scheduled
            </Badge>
          ) : null}
        </div>
        <Button
          variant="ghost"
          size="icon"
          data-testid={`video-delete-${v.id}`}
          onClick={() => onDelete(v.id)}
          className="h-8 w-8 text-red-600 hover:bg-red-50"
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </CardContent>
      {/* Phase1Observability1:progress_eta_v1 — auto-hides on terminal */}
      <div className="px-3 pb-3">
        <JobProgressBar video={v} compact />
      </div>
    </Card>
  );

  return (
    <div data-testid="page-library" className="mx-auto max-w-6xl space-y-5">
      <KillswitchBanner />
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-stone-800">Library</h1>
          <p className="text-sm text-stone-600">
            Every video you generate lives here.
          </p>
        </div>
        <Button asChild className="bg-gradient-to-r from-orange-500 to-rose-500 text-white">
          <Link to="/create">
            <PlusCircle className="mr-2 h-4 w-4" />
            New video
          </Link>
        </Button>
      </div>

      {/* Filters */}
      <div
        data-testid="library-filters"
        className="flex flex-wrap items-center gap-2 rounded-xl border border-orange-200 bg-white/80 p-3 shadow-sm"
      >
        <span className="text-xs font-semibold uppercase tracking-wide text-stone-500">
          Type
        </span>
        {filterOptions.map((o) => (
          <button
            key={o.k}
            data-testid={`filter-type-${o.k}`}
            onClick={() => setType(o.k)}
            className={`rounded-full px-3 py-1 text-xs font-medium transition ${
              type === o.k
                ? "bg-orange-500 text-white"
                : "bg-orange-50 text-stone-700 hover:bg-orange-100"
            }`}
          >
            {o.l}
          </button>
        ))}
        <div className="mx-2 h-5 w-px bg-orange-200" />
        <span className="text-xs font-semibold uppercase tracking-wide text-stone-500">
          Status
        </span>
        {statusOptions.map((o) => (
          <button
            key={o.k}
            data-testid={`filter-status-${o.k}`}
            onClick={() => setStatus(o.k)}
            className={`rounded-full px-3 py-1 text-xs font-medium transition ${
              status === o.k
                ? "bg-orange-500 text-white"
                : "bg-orange-50 text-stone-700 hover:bg-orange-100"
            }`}
          >
            {o.l}
          </button>
        ))}
        <div className="mx-2 h-5 w-px bg-orange-200" />
        <span className="text-xs font-semibold uppercase tracking-wide text-stone-500">
          Quality
        </span>
        {[
          { k: "all", l: "All" },
          { k: "animated", l: "🎬 Fully Animated" },
          { k: "fallbacks", l: "⚠ Has Fallbacks" },
          { k: "retry", l: "⏳ Awaiting Retry" },
          { k: "unavailable", l: "❌ Provider Unavailable" },
        ].map((o) => (
          <button
            key={o.k}
            data-testid={`filter-quality-${o.k}`}
            onClick={() => setQualityFilter(o.k)}
            className={`rounded-full px-3 py-1 text-xs font-medium transition ${
              qualityFilter === o.k
                ? "bg-amber-500 text-white"
                : "bg-amber-50 text-stone-700 hover:bg-amber-100"
            }`}
          >
            {o.l}
          </button>
        ))}
        <div className="mx-2 h-5 w-px bg-orange-200" />
        <span className="text-xs font-semibold uppercase tracking-wide text-stone-500">
          Sort
        </span>
        {[
          { k: "episode", l: "Episode ↑" },
          { k: "newest", l: "Newest" },
        ].map((o) => (
          <button
            key={o.k}
            data-testid={`sort-${o.k}`}
            onClick={() => setSortMode(o.k)}
            className={`rounded-full px-3 py-1 text-xs font-medium transition ${
              sortMode === o.k
                ? "bg-stone-700 text-white"
                : "bg-stone-100 text-stone-700 hover:bg-stone-200"
            }`}
          >
            {o.l}
          </button>
        ))}
        <button
          data-testid="group-by-type"
          onClick={() => setGroupByType((g) => !g)}
          className={`ml-1 rounded-full px-3 py-1 text-xs font-medium transition ${
            groupByType
              ? "bg-stone-700 text-white"
              : "bg-stone-100 text-stone-700 hover:bg-stone-200"
          }`}
        >
          {groupByType ? "Grouped" : "Group by type"}
        </button>
      </div>

      {videos.length === 0 ? (
        <div
          data-testid="library-empty"
          className="mx-auto mt-8 max-w-xl rounded-2xl border border-dashed border-orange-300 bg-white/70 p-10 text-center shadow-sm"
        >
          <FolderOpen className="mx-auto mb-3 h-10 w-10 text-orange-500" />
          <h2 className="text-xl font-bold text-stone-800">No videos yet</h2>
          <p className="mt-1 text-sm text-stone-600">
            Create your first video to see it here.
          </p>
          <Button asChild className="mt-4 bg-gradient-to-r from-orange-500 to-rose-500 text-white">
            <Link to="/create">Create your first!</Link>
          </Button>
        </div>
      ) : groupByType && groupedVideos ? (
        <div data-testid="library-grouped" className="space-y-6">
          {[
            { k: "music", l: "🎵 Music" },
            { k: "story", l: "📖 Story" },
            { k: "learning", l: "🧠 Learning" },
            { k: "other", l: "Other" },
          ].map((g) =>
            (groupedVideos[g.k] || []).length === 0 ? null : (
              <div key={g.k} data-testid={`library-group-${g.k}`}>
                <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-stone-600">
                  {g.l}
                  <span className="ml-2 rounded-full bg-stone-100 px-2 py-0.5 text-xs text-stone-600">
                    {groupedVideos[g.k].length}
                  </span>
                </h2>
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {groupedVideos[g.k].map((v) => renderCard(v))}
                </div>
              </div>
            ),
          )}
        </div>
      ) : (
        <div
          data-testid="library-grid"
          className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3"
        >
          {sortedVideos.map((v) => renderCard(v))}
        </div>
      )}

      {/* Publish dialog */}
      {publishOpen ? (
        <PublishDialog
          video={publishOpen}
          ytConnected={ytConnected}
          onClose={() => {
            setPublishOpen(null);
            refresh();
          }}
        />
      ) : null}

      {/* Detail modal */}
      {open ? (
        <div
          data-testid="video-detail"
          className="fixed inset-0 z-40 flex items-center justify-center bg-stone-900/50 p-4"
          onClick={() => setOpen(null)}
        >
          <div
            className="max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-2xl bg-white p-6 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-3 flex items-start justify-between gap-4">
              <div>
                <h2 className="text-xl font-bold text-stone-800">{open.title}</h2>
                <p className="text-xs text-stone-500">
                  {open.type} · {open.length} · {open.duration_sec || previewDur || "—"}s
                </p>
                <div className="mt-1 flex flex-wrap gap-1.5">
                  <QualityBadge video={open} />
                  {open.animation_quality_target ? (
                    <span className="rounded bg-stone-100 px-2 py-0.5 text-[11px] text-stone-700">
                      mode: {open.animation_quality_target}
                    </span>
                  ) : null}
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setOpen(null)}
                data-testid="close-detail"
              >
                Close
              </Button>
            </div>
            {/* Phase1Observability1:progress_eta_v1 — show progress at the top of every modal (auto-hides on terminal) */}
            <div className="mb-3">
              <JobProgressBar video={open} />
            </div>
            {(open.status === "awaiting_retry" || open.awaiting_retry) ? (
              <div
                data-testid="detail-retry-banner"
                className="mb-3 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900"
              >
                <div className="font-semibold">
                  ⏳ Awaiting retry — attempt {open.retry_attempts || 0}/5
                </div>
                <div>
                  Failed step:{" "}
                  <code>{open.failed_step || "?"}</code> · next attempt:{" "}
                  {open.next_retry_at
                    ? new Date(open.next_retry_at).toLocaleString()
                    : "soon"}
                </div>
                {open.last_retry_error ? (
                  <div className="mt-1 truncate">
                    Last error: {open.last_retry_error}
                  </div>
                ) : null}
              </div>
            ) : null}
            {open.status === "ready" && hasFallbacks(open) ? (
              <div className="mb-3 flex items-center justify-between rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs">
                <span className="text-amber-900">
                  This video uses{" "}
                  {open.clip_provider === "ken_burns" ? "Ken Burns stills" : "fallback assets"}.
                  Regenerate with real animation when fal.ai is healthy.
                </span>
                <Button
                  size="sm"
                  data-testid={`regen-clips-${open.id}`}
                  disabled={regenLoading}
                  onClick={async () => {
                    setRegenLoading(true);
                    try {
                      await api.post(`/videos/${open.id}/regenerate-clips`);
                      setOpen(null);
                      refresh();
                    } finally {
                      setRegenLoading(false);
                    }
                  }}
                  className="bg-amber-600 text-white hover:bg-amber-700"
                >
                  {regenLoading ? (
                    <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                  ) : (
                    <Wand2 className="mr-1 h-3 w-3" />
                  )}
                  Regenerate
                </Button>
              </div>
            ) : null}
            {open.status === "needs_human" ? (
              <div
                data-testid="detail-needs-human"
                className="mb-3 space-y-3 rounded-lg border border-rose-300 bg-rose-50 p-4"
              >
                <div className="flex items-center gap-2 text-sm font-semibold text-rose-700">
                  <AlertCircle className="h-4 w-4" />
                  Needs your review
                </div>
                <div className="text-sm text-stone-700">
                  {humanReviewReason(open.needs_human_reason) || open.needs_human_reason || "A guard flagged this video."}
                  {open.needs_human_at ? (
                    <span className="text-stone-500">
                      {" "}· flagged {new Date(open.needs_human_at).toLocaleString()}
                    </span>
                  ) : null}
                </div>
                {parseGuardChips(open.needs_human_reason).length > 0 ? (
                  <div className="flex flex-wrap gap-1.5">
                    {parseGuardChips(open.needs_human_reason).map((g) => (
                      <span
                        key={g.code}
                        data-testid={`guard-chip-${g.code}`}
                        title={g.code}
                        className="inline-flex items-center rounded-full border border-rose-300 bg-rose-100 px-2 py-0.5 text-[11px] font-medium text-rose-800"
                      >
                        {g.code} · {g.label}
                      </span>
                    ))}
                  </div>
                ) : null}
                {/* Phase1: fallback to canonical /api/videos/{id}/thumbnail when DB field is null (legacy docs). onError hides if asset truly missing. */}
                <img
                  data-testid="needs-human-preview-thumbnail"
                  src={absUrl(open.thumbnail_url || `/api/videos/${open.id}/thumbnail`)}
                  alt=""
                  onError={(e) => { e.currentTarget.style.display = "none"; }}
                  className="w-full max-w-sm rounded-md border border-rose-200"
                />
                {/* Phase1: fallback to canonical /api/videos/{id}/file when DB field is null (legacy docs). SafeVideo handles 404s gracefully. */}
                <div className="space-y-1">
                  <SafeVideo
                    key={`needshuman-${open.id}`}
                    data-testid="needs-human-preview-video"
                    controls
                    className="w-full rounded-md border border-rose-200"
                    src={absUrl(open.video_url || `/api/videos/${open.id}/file`)}
                    poster={absUrl(
                      open.custom_thumbnail_url
                        || open.thumbnail_url
                        || `/api/videos/${open.id}/thumbnail`
                    )}
                    onLoadedMetadata={(e) => {
                      const d = e.currentTarget?.duration;
                      if (Number.isFinite(d) && d > 0) {
                        setPreviewDur(d.toFixed(1));
                      }
                    }}
                    onError={(e) => {
                      const code = e.currentTarget?.error?.code;
                      // 4 = MEDIA_ELEMENT_ERROR_SRC_NOT_SUPPORTED — typically
                      // the GET /file returned 404 or a non-mp4. Surface that
                      // clearly instead of leaving a black grey box.
                      setPreviewError(
                        code === 4
                          ? "No preview file on disk for this attempt."
                          : `Preview failed to load (code=${code ?? "?"}).`,
                      );
                    }}
                  />
                  {previewError ? (
                    <p
                      data-testid="needs-human-preview-error"
                      className="text-xs italic text-rose-600"
                    >
                      {previewError}
                    </p>
                  ) : (
                    <p className="text-xs italic text-stone-500">
                      Preview of failed attempt — not a published version.
                    </p>
                  )}
                </div>
              </div>
            ) : null}
            {open.status === "ready" && open.video_url ? (
              <SafeVideo
                key={open.id}
                data-testid="detail-video"
                controls
                className="w-full rounded-lg border border-orange-100"
                src={absUrl(open.video_url)}
                onLoadedMetadata={(e) => {
                  const d = e.currentTarget?.duration;
                  if (Number.isFinite(d) && d > 0) {
                    setPreviewDur(d.toFixed(1));
                  }
                }}
              />
            ) : ["needs_human", "failed"].includes(open.status) ? (
              /* Phase1: preview already rendered inside the rose review
                 block above for needs_human/failed. The outer placeholder
                 ("Asset unavailable for preview…") was double-rendering
                 below the working video, confusing the reviewer. */
              null
            ) : (
              <div className="rounded-lg border border-dashed border-orange-200 px-6 py-16 text-center text-sm text-stone-500">
                {`Video is ${open.status} — check back in a moment.`}
              </div>
            )}
            {open.type === "music"
              && open.status === "ready"
              && hasFallbacks(open)
              && open.srt_path ? (
              <div className="mt-4">
                <AnchorConfirmPanel
                  video={open}
                  onRestitched={() => refresh()}
                />
              </div>
            ) : null}
            {open.description ? (
              <p className="mt-4 whitespace-pre-line text-sm text-stone-700">
                {open.description}
              </p>
            ) : null}
            {open.tags && open.tags.length > 0 ? (
              <div data-testid="detail-tags" className="mt-3 flex flex-wrap gap-1">
                <Tag className="mr-1 h-3 w-3 shrink-0 self-center text-stone-400" />
                {open.tags.map((t) => (
                  <span
                    key={t}
                    className="rounded-full bg-orange-100 px-2 py-0.5 text-[11px] font-medium text-orange-800"
                  >
                    {t}
                  </span>
                ))}
              </div>
            ) : null}
            {open.active_character_name ? (
              <div className="mt-2 text-xs text-stone-500">
                Starring: <strong>{open.active_character_name}</strong>
                {open.prompt_version ? (
                  <span className="ml-2 text-stone-400">
                    · {open.prompt_version}
                  </span>
                ) : null}
              </div>
            ) : null}
            {open.cost ? (
              <details
                data-testid="cost-breakdown"
                className="mt-3 rounded-md border border-orange-100 bg-orange-50/40 p-2 text-xs text-stone-700"
              >
                <summary className="cursor-pointer font-semibold">
                  Cost: ${open.cost.total?.toFixed(2)} USD
                  {open.clip_provider === "fal_hailuo02" ? (
                    <span className="ml-2 rounded bg-purple-100 px-1.5 py-0.5 text-[10px] font-medium text-purple-700">
                      Hailuo 02 animated · {open.clip_stats?.animated}/
                      {open.scene_count}
                    </span>
                  ) : (
                    <span className="ml-2 rounded bg-stone-100 px-1.5 py-0.5 text-[10px] font-medium text-stone-700">
                      Ken Burns · still images
                    </span>
                  )}
                </summary>
                <div className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1">
                  <div>Script (LLM)</div>
                  <div className="text-right">${open.cost.script?.toFixed(3)}</div>
                  <div>Images (nano-banana)</div>
                  <div className="text-right">${open.cost.images?.toFixed(3)}</div>
                  <div>Animated clips (fal.ai)</div>
                  <div className="text-right">${open.cost.clips?.toFixed(3)}</div>
                  <div>Voiceover (ElevenLabs)</div>
                  <div className="text-right">${open.cost.voice?.toFixed(3)}</div>
                  <div>Music (Suno)</div>
                  <div className="text-right">${open.cost.music?.toFixed(3)}</div>
                </div>
              </details>
            ) : null}
            {open.lyric_scene_map && open.lyric_scene_map.length > 0 ? (
              <details
                data-testid="lyric-scene-map"
                className="mt-3 rounded-md border border-sky-100 bg-sky-50/40 p-2 text-xs text-stone-700"
                open
              >
                <summary className="cursor-pointer font-semibold">
                  Lyric → Scene mapping
                  <span
                    className={`ml-2 rounded px-1.5 py-0.5 text-[10px] font-medium ${
                      (open.lyric_scene_avg_confidence ?? 0) >= 0.75
                        ? "bg-emerald-100 text-emerald-700"
                        : (open.lyric_scene_avg_confidence ?? 0) >= 0.6
                        ? "bg-amber-100 text-amber-700"
                        : "bg-red-100 text-red-700"
                    }`}
                  >
                    avg confidence{" "}
                    {(open.lyric_scene_avg_confidence ?? 0).toFixed(2)}
                  </span>
                  <span className="ml-2 text-[10px] text-stone-500">
                    {open.lyric_scene_map.length} verses ·{" "}
                    {open.lyric_scene_windows?.length ?? 0} windows
                  </span>
                </summary>
                <div className="mt-2 max-h-80 overflow-y-auto rounded border border-sky-100 bg-white">
                  <table className="w-full text-[11px]">
                    <thead className="sticky top-0 bg-sky-100/80 text-stone-700">
                      <tr>
                        <th className="px-2 py-1 text-left">Time</th>
                        <th className="px-2 py-1 text-left">Verse</th>
                        <th className="px-2 py-1 text-center">Scene</th>
                        <th className="px-2 py-1 text-left">Concept</th>
                        <th className="px-2 py-1 text-right">Conf.</th>
                      </tr>
                    </thead>
                    <tbody>
                      {open.lyric_scene_map.map((m, i) => {
                        const low = (m.match_confidence ?? 0) < 0.4;
                        return (
                          <tr
                            key={i}
                            data-testid={`lsm-row-${i}`}
                            className={`border-t border-sky-50 ${
                              low ? "bg-amber-50" : ""
                            }`}
                          >
                            <td className="whitespace-nowrap px-2 py-1 font-mono text-stone-500">
                              {m.start?.toFixed(1)}-{m.end?.toFixed(1)}s
                            </td>
                            <td className="px-2 py-1">{m.verse_text}</td>
                            <td className="px-2 py-1 text-center font-semibold text-sky-800">
                              #{m.scene_number}
                            </td>
                            <td className="px-2 py-1 text-stone-600">
                              {m.scene_concept}
                            </td>
                            <td className="px-2 py-1 text-right font-mono">
                              {(m.match_confidence ?? 0).toFixed(2)}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                {open.intro_meta ? (
                  <div className="mt-2 text-[11px] text-stone-500">
                    Intro:{" "}
                    <strong className="text-stone-700">
                      {open.intro_meta.show_title}
                    </strong>{" "}
                    · {open.intro_meta.episode} ·{" "}
                    {open.intro_meta.intro_total_duration?.toFixed(1)}s
                  </div>
                ) : null}
              </details>
            ) : null}
            {["ready", "needs_human", "failed"].includes(open.status) ? (
              <div className="mt-4 flex flex-wrap gap-2">
                {open.status === "ready" ? (
                  <Button
                    className="bg-red-600 text-white hover:bg-red-700"
                    data-testid="detail-publish"
                    onClick={() => setPublishOpen(open)}
                  >
                    <Youtube className="mr-2 h-4 w-4" />
                    {open.publish_status?.status === "published"
                      ? "Re-publish"
                      : open.publish_status?.status === "scheduled"
                      ? "Update schedule"
                      : "Publish to YouTube"}
                  </Button>
                ) : null}
                {open.status === "ready" && open.publish_status?.youtube_url ? (
                  <Button
                    asChild
                    variant="outline"
                    data-testid="detail-yt-link"
                    className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                  >
                    <a
                      href={open.publish_status.youtube_url}
                      target="_blank"
                      rel="noreferrer"
                    >
                      <ExternalLink className="mr-2 h-4 w-4" /> View on YouTube
                    </a>
                  </Button>
                ) : null}
                {/* Phase1: always show download for terminal statuses; fall back to canonical /file?download=1 when DB fields are null (legacy docs). */}
                {["ready", "needs_human", "failed"].includes(open.status) ? (
                  <Button
                    asChild
                    className="bg-stone-800 text-white hover:bg-stone-700"
                    data-testid="detail-download"
                  >
                    <a
                      href={absUrl(
                        open.download_url
                          || open.video_url
                          || `/api/videos/${open.id}/file?download=1`
                      )}
                      download
                    >
                      <Download className="mr-2 h-4 w-4" /> Download MP4
                    </a>
                  </Button>
                ) : null}
                {/* Phase1: show captions for ready (existing) + offer fallback canonical endpoint on needs_human/failed legacy docs. May 404 if SRT genuinely missing. */}
                {open.srt_url || ["needs_human", "failed"].includes(open.status) ? (
                  <Button
                    asChild
                    variant="outline"
                    data-testid="detail-srt-download"
                    className="border-sky-300 text-sky-700 hover:bg-sky-50"
                    title={`${open.srt_entry_count || 0} caption cues`}
                  >
                    <a
                      href={absUrl(
                        open.srt_url
                          ? `${open.srt_url}?download=1`
                          : `/api/videos/${open.id}/captions.srt?download=1`
                      )}
                      download
                    >
                      <Download className="mr-2 h-4 w-4" />
                      Captions (.srt)
                    </a>
                  </Button>
                ) : null}
                <RegenerateVideoButton
                  videoId={open.id}
                  videoTitle={open.title}
                  className="border-orange-300"
                  onRegenerated={(newDoc) => {
                    setOpen(null);
                    refresh();
                    window.scrollTo({ top: 0, behavior: "smooth" });
                    if (newDoc?.id) {
                      // Lightweight toast-replacement via window event;
                      // existing alert() removed to avoid blocking the UI.
                      console.info("[regen] queued", newDoc.id);
                    }
                  }}
                />
                {(open.status === "needs_human" || open.status === "failed") ? (
                  <ManualPromoteButton
                    videoId={open.id}
                    videoTitle={open.title}
                    gateReason={open.needs_human_reason || open.failed_reason}
                    className="border-emerald-300 text-emerald-800 hover:bg-emerald-50"
                    onPromoted={(doc) => {
                      setOpen(doc);
                      refresh();
                    }}
                  />
                ) : null}
                {(open.status === "needs_human" || open.status === "failed") ? (
                  <Button
                    data-testid="detail-delete"
                    onClick={() => onDelete(open.id)}
                    className="bg-red-600 text-white hover:bg-red-700"
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    Delete
                  </Button>
                ) : null}
              </div>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}
